'use client';

import React, { useCallback } from 'react';
import type { ModalProviderValue } from '@dr-pam/common-components/Components/Modal/ModalProvider';
import { useModalProvider } from '@dr-pam/common-components/Components/Modal/ModalProvider';
import Button from '../form/Button';
import type { ActiveUpgradePrompt } from '../../services/client/ClientUpgradeService';

const MODAL_ID = 'upgrade-prompt';

export type UpgradeModalProps = {
	prompt: ActiveUpgradePrompt;
	onEngage: (promptId: string) => Promise<string | null>;
};

export default function UpgradeModal({ prompt, onEngage }: UpgradeModalProps) {
	const modalProvider = useModalProvider();

	const { title, message, ctaText } = prompt.prompt;
	const displayCtaText = ctaText || 'Upgrade';

	const handleCtaClick = useCallback(async () => {
		let engagementId: string | null = null;

		try {
			engagementId = await onEngage(prompt.prompt.id);
		} catch (error) {
			console.error('[UpgradeModal] Failed to record engagement:', error);
		} finally {
			modalProvider.closeModal(MODAL_ID);

			const href = engagementId ? `/upgrade?engagementId=${encodeURIComponent(engagementId)}` : '/upgrade';
			window.location.assign(href);
		}
	}, [modalProvider, onEngage, prompt.prompt.id]);

	return (
		<div className="upgrade-modal">
			<div className="upgrade-modal__content">
				{title && <h2 className="upgrade-modal__title">{title}</h2>}
				{message && (
					<div
						className="upgrade-modal__message large-paragraph"
						dangerouslySetInnerHTML={{ __html: message }}
					/>
				)}

				<div className="upgrade-modal__actions flex jc">
					<Button className="Button dark solid" onClick={handleCtaClick}>
						{displayCtaText}
					</Button>
				</div>
			</div>
		</div>
	);
}

export function showUpgradeModal(
	modalProvider: ModalProviderValue,
	prompt: ActiveUpgradePrompt,
	onEngage: (promptId: string) => Promise<string | null>,
) {
	modalProvider.openModal({
		modalId: MODAL_ID,
		className: 'popup-overlay blur',
		showClose: true,
		children: <UpgradeModal prompt={prompt} onEngage={onEngage} />,
	});
}
